<template>
    <div :class="$attrs.class">
        <Popper
            :placement="popperPlacement"
            arrow
            block
            hover
            disable-pointer-events
            :content="errorMessage"
            class="text-14">
            <div class="flex items-center gap-3">
                <div class="form-input flex-1">
                    <input
                        :id="fieldId"
                        ref="inputField"
                        v-autofocus="autofocus"
                        v-prohibit-zoom
                        class="w-full"
                        :class="{ 'uppercase': uppercase }"
                        :style="{ 'color': colorPicker ? (inputValue + '') : 'inherit' }"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                        data-lpignore="true"
                        v-bind="fieldAttrs"
                        tabindex="0"
                        :placeholder="placeholder"
                        :type="isPasswordType ? 'password' : 'text'"
                        :name="name"
                        :value="inputValue"
                        :has-error="!!errorMessage || hasError"
                        :has-label="!!label"
                        :has-value="inputValue === 0 || !!inputValue"
                        :disabled="disabled"
                        :readonly="readonly || !!value"
                        v-on="validationListeners"
                        @focus="isFocused = true">

                    <label v-if="label"
                           :for="fieldId"
                           :class="{ active: (inputValue === 0 || !!inputValue) && !colorPicker }"
                           class="truncate">
                        {{ label }}
                    </label>

                    <span v-if="icon"
                          class="input-search text-13 font-medium"
                          @click="focusInput()">
                        {{ icon }}
                    </span>

                    <Popper
                        v-if="colorPicker"
                        placement="right"
                        block
                        click
                        offset-distance="40"
                        no-padding
                        class="input-search z-spinner"
                        :style="{ 'color': inputValue }">
                        <CIcon class="color-picker"
                               name="palette"
                               width="22"
                               height="22"
                               fill/>

                        <template #content>
                            <ColorPicker v-model="inputValue" @change-color="e => $emit('change-color', e)"/>
                        </template>
                    </Popper>

                    <CIcon v-else-if="isPasswordType"
                           name="eye"
                           class="input-password-eye cursor-pointer"
                           width="16"
                           height="16"
                           fill
                           @click="togglePasswordVisiblity()"/>

                    <CIcon v-else-if="isSearchType"
                           class="input-search"
                           name="icon-search"
                           width="16"
                           height="16"
                           fill
                           @click="focusInput()"/>

                    <InputClear
                        v-else-if="!disabled && !readonly && inputClear && !isNumberType && !!inputValue && !icon"
                        class="input-clear hidden md:inline-block"
                        @clear="handleClear"/>
                </div>
            
                <!-- <template v-if="!hideErrorIndicator">
                    <CIcon v-if="hasError || errorMessage"
                           class="text-red-500"
                           name="close-circle"
                           height="30"
                           width="30"/>
                    <CIcon v-else
                           class="text-green-primary"
                           name="check-circle"
                           height="30"
                           width="30"/>
                </template> -->
            </div>
        </Popper>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import { useField } from 'vee-validate';

enum Currencies {
    EURO = 'EUR',
    USD = 'USD',
    DKK = 'DKK',
    GBP = 'GBP',
    NOK = 'NOK',
    SEK = 'SEK'
}

export default defineComponent({
    name: 'InputText',
    inheritAttrs: false,
    props: {
        type: {
            type: String,
            default: 'text',
        },
        value: {
            type: null,
        },
        // eslint-disable-next-line vue/require-default-prop
        modelValue: {
            type: null,
        },
        name: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: undefined,
        },
        popperPlacement: {
            type: String,
            default: 'top',
        },
        icon: {
            type: String,
            required: false,
        },
        currency: {
            type: String as PropType<Currencies>,
            required: false,
        },
        hideErrorIndicator: {
            type: Boolean,
            required: false,
            default: false,
        },
        colorPicker: {
            type: Boolean,
            required: false,
            default: false,
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasError: {
            type: Boolean,
            required: false,
            default: false,
        },
        uppercase: {
            type: Boolean,
            required: false,
            default: false,
        },
        inputClear: {
            type: Boolean,
            required: false,
            default: true,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false,
        },
        applySmileys: {
            type: Boolean,
            required: false,
            default: false,
        },
        validationBehaviour: {
            type: String,
            default: 'aggressive',
            validate: (value: string): boolean =>
                ['aggressive', 'eager'].includes(value),
        },
        rules: {
            type: [Object, String] as Object as PropType<{ [rule: string]: any } | string>,
            required: false,
        },
    },
    setup(props, { attrs, emit }) {
        const isFocused = ref(false);
        const inputField = ref<HTMLInputElement | null>(null);

        const {
            value: inputValue,
            errorMessage,
        } = useField<string | number>(props.name, computed(() => props.rules), {
            initialValue: props.value || props.modelValue,
            validateOnValueUpdate: true,
            validateOnMount: true,
            syncVModel: true,
            label: props.label,
        });

        const fieldId = computed(() => {
            return props.name + Math.random();
        });

        const isSearchType = computed(() => {
            return props.type === 'search';
        });

        const isNumberType = computed(() => {
            return props.type === 'number';
        });

        const isPasswordType = computed(() => {
            return props.type === 'password';
        });

        const fieldAttrs = computed(() => {
            const fieldAttrs = {
                ...attrs,
                class: '',
            };
            return fieldAttrs;
        });

        const togglePasswordVisiblity = () => {
            if (inputField.value){
                inputField.value.type = inputField.value.type === 'text' ? 'password' : 'text';
            }
        };

        const focusInput = () => {
            if (inputField.value) {
                inputField.value.focus();

                // Stupid hack to place mouse at end of input value
                const val = inputField.value.value;
                if (val) {
                    inputField.value.value = '';
                    inputField.value.value = val;
                }
            }
        };

        const handleClear = () => {
            inputValue.value = '';
            emit('clear');
            focusInput();
        };

        const formatText = (text: string) => {
            if (props.applySmileys && text?.length > 1) {
                // Replace :) or :-) with smiley
                text = text.replace(/:-?\)/g, '😊');
                // Replace :D or :-D with big smiley
                text = text.replace(/:-?D/g, '😁');
                // Replace :( or :-( with sad smiley
                text = text.replace(/:-?\(/g, '😔');
                // Replace :P or :-P with tongue-out smiley
                text = text.replace(/:-?P/g, '😋');
                // Replace x) or x-) with wink smiley
                text = text.replace(/x(-?D|-\))/g, '😝');
            }

            return text;
        };

        const getValue = (evt: { target: { value: any }}) => {
            const value = evt.target.value;
            if (isNumberType.value && typeof value === 'string') {
                if (value.length === 0)
                    return null!;
            
                const x = value.replace(',', '.');
                const n = parseFloat(x);
                return isNaN(n) ? x : n;
            }

            return formatText(value);
        };

        const handleInputEvent = (evt) => {
            isFocused.value = true;

            if (evt && !props.readonly) {
                inputValue.value = getValue(evt);
            }
        };

        const handleChangeEvent = (evt) => {
            isFocused.value = true;

            if (evt && !props.readonly) {
                inputValue.value = getValue(evt);
            }
        };

        const handleBlurEvent = () => {
            isFocused.value = false;
        };

        const validationListeners = computed(() => {
            // if no current errors use lazy validation handlers
            const listeners = {
                blur: handleBlurEvent,
                change: handleChangeEvent,
                input: handleInputEvent,
            };

            // if aggressive mode or current errors, replace with eager handlers to validate changes immediately
            if (props.validationBehaviour === 'aggressive' || errorMessage.value) {
                listeners.input = handleChangeEvent;
            }

            return listeners;
        });

        return {
            inputField,
            focusInput,
            validationListeners,
            errorMessage,
            inputValue,
            fieldId,
            isSearchType,
            isNumberType,
            isPasswordType,
            handleClear,
            fieldAttrs,
            isFocused,
            togglePasswordVisiblity,
        };
    },
});
</script>

<style lang="scss" scoped>
@use "@/styling/6-forms/forms.scss";

.color-picker {
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
}
</style>
